import React, { useState, useEffect } from 'react';
import { X, Trash2, Save, AlertTriangle, Wand2, ExternalLink, Eye, EyeOff } from 'lucide-react';
import {
  BlogPost,
  BlogPostInput,
  createPost,
  updatePost,
  deletePost,
  isSlugTaken,
  slugify,
} from '../../services/blog';

interface Props {
  post: BlogPost | null; // null = create new
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

const EMPTY: BlogPostInput = {
  slug: '',
  title: '',
  excerpt: '',
  content: '',
  author: 'Tõuks.ee',
  tags: [],
  image_url: '',
  published_at: new Date().toISOString(),
  read_time_minutes: 5,
  meta_title: '',
  meta_description: '',
};

function toDatetimeLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}

export default function BlogEditDrawer({ post, onClose, onSaved, onDeleted }: Props) {
  const isNew = post === null;
  const [form, setForm] = useState<BlogPostInput>(EMPTY);
  const [tagsText, setTagsText] = useState('');
  const [published, setPublished] = useState(true);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (post) {
      setForm({
        slug: post.slug,
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        author: post.author,
        tags: post.tags || [],
        image_url: post.image_url,
        published_at: post.published_at,
        read_time_minutes: post.read_time_minutes,
        meta_title: post.meta_title || '',
        meta_description: post.meta_description || '',
      });
      setTagsText((post.tags || []).join(', '));
      setPublished(!!post.published_at);
    } else {
      setForm(EMPTY);
      setTagsText('');
      setPublished(true);
    }
  }, [post]);

  const set = <K extends keyof BlogPostInput>(field: K, value: BlogPostInput[K]) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleGenerateSlug = () => set('slug', slugify(form.title));

  const handleSave = async () => {
    setError('');

    if (!form.title.trim()) return setError('Pealkiri on kohustuslik.');
    if (!form.slug.trim()) return setError('URL slug on kohustuslik.');
    if (!form.excerpt.trim()) return setError('Sissejuhatus (excerpt) on kohustuslik.');

    setSaving(true);
    try {
      const taken = await isSlugTaken(form.slug.trim(), post?.id);
      if (taken) {
        setSaving(false);
        return setError('See URL slug on juba kasutuses. Vali teine.');
      }

      const tags = tagsText
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const payload: BlogPostInput = {
        ...form,
        slug: form.slug.trim(),
        title: form.title.trim(),
        tags,
        published_at: published ? form.published_at || new Date().toISOString() : null,
        meta_title: form.meta_title?.trim() || null,
        meta_description: form.meta_description?.trim() || null,
        read_time_minutes: Number(form.read_time_minutes) || 5,
      };

      if (isNew) {
        await createPost(payload);
      } else {
        await updatePost(post!.id, payload);
      }
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Salvestamine ebaõnnestus');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!post) return;
    setDeleting(true);
    setError('');
    try {
      await deletePost(post.id);
      onDeleted();
    } catch (err: any) {
      setError(err.message || 'Kustutamine ebaõnnestus');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-3xl bg-gray-50 h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {isNew ? 'Uus blogipostitus' : 'Muuda postitust'}
            </h2>
            {!isNew && (
              <a
                href={`/blogi/${post!.slug}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-[#4FC1E9] hover:underline inline-flex items-center gap-1"
              >
                Vaata lehel <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-900 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 flex-1">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Basics */}
          <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm">Põhiväli</h3>

            <Field label="Pealkiri (H1)">
              <input value={form.title} onChange={(e) => set('title', e.target.value)} className="admin-input" />
            </Field>

            <Field label="URL slug">
              <div className="flex gap-2">
                <span className="text-sm text-gray-400 self-center whitespace-nowrap">/blogi/</span>
                <input value={form.slug} onChange={(e) => set('slug', e.target.value)} className="admin-input" />
                <button
                  type="button"
                  onClick={handleGenerateSlug}
                  title="Genereeri pealkirjast"
                  className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:border-[#4FC1E9] hover:text-[#4FC1E9] whitespace-nowrap"
                >
                  <Wand2 className="w-4 h-4" />
                  Genereeri
                </button>
              </div>
            </Field>

            <Field label="Sissejuhatus / excerpt (kuvatakse blogi loendis)">
              <textarea
                value={form.excerpt}
                onChange={(e) => set('excerpt', e.target.value)}
                rows={3}
                className="admin-input resize-none"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Autor">
                <input value={form.author} onChange={(e) => set('author', e.target.value)} className="admin-input" />
              </Field>
              <Field label="Lugemisaeg (min)">
                <input
                  type="number"
                  min={1}
                  value={form.read_time_minutes}
                  onChange={(e) => set('read_time_minutes', Number(e.target.value))}
                  className="admin-input"
                />
              </Field>
            </div>

            <Field label="Sildid / tags (eralda komadega)">
              <input
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
                placeholder="Algajatele, Ostujuhend, Tehniline juhend"
                className="admin-input"
              />
            </Field>
          </section>

          {/* Image */}
          <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm">Pilt</h3>
            <Field label="Pildi URL (kaanepilt)">
              <input
                value={form.image_url}
                onChange={(e) => set('image_url', e.target.value)}
                placeholder="https://..."
                className="admin-input"
              />
            </Field>
            {form.image_url && (
              <img
                src={form.image_url}
                alt="Eelvaade"
                className="w-full h-48 object-cover rounded-lg border border-gray-200"
              />
            )}
          </section>

          {/* Content */}
          <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm">Sisu (HTML)</h3>
            <p className="text-xs text-gray-400">
              Kasuta HTML-i: &lt;p&gt;, &lt;h2&gt;, &lt;h3&gt;, &lt;ul&gt;/&lt;li&gt;, &lt;table&gt;, &lt;strong&gt;,
              &lt;a href="..."&gt;. Sama vorming nagu olemasolevatel postitustel.
            </p>
            <textarea
              value={form.content}
              onChange={(e) => set('content', e.target.value)}
              rows={20}
              className="admin-input font-mono text-xs leading-relaxed"
            />
          </section>

          {/* SEO */}
          <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm">SEO metaandmed</h3>
            <Field label={`Meta pealkiri (${(form.meta_title || '').length} tähemärki, soovitus ~60)`}>
              <input
                value={form.meta_title || ''}
                onChange={(e) => set('meta_title', e.target.value)}
                placeholder="Kui tühi, kasutatakse pealkirja"
                className="admin-input"
              />
            </Field>
            <Field label={`Meta kirjeldus (${(form.meta_description || '').length} tähemärki, soovitus ~155)`}>
              <textarea
                value={form.meta_description || ''}
                onChange={(e) => set('meta_description', e.target.value)}
                rows={3}
                placeholder="Kui tühi, kasutatakse sissejuhatust"
                className="admin-input resize-none"
              />
            </Field>
          </section>

          {/* Publishing */}
          <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm">Avaldamine</h3>
            <button
              type="button"
              onClick={() => setPublished((p) => !p)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                published
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              {published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {published ? 'Avaldatud (nähtav)' : 'Mustand (peidetud)'}
            </button>
            {published && (
              <Field label="Avaldamise kuupäev">
                <input
                  type="datetime-local"
                  value={toDatetimeLocal(form.published_at)}
                  onChange={(e) =>
                    set('published_at', e.target.value ? new Date(e.target.value).toISOString() : null)
                  }
                  className="admin-input"
                />
              </Field>
            )}
            <p className="text-xs text-gray-400">
              Mustandeid ei kuvata avalikul lehel ega otsingumootoritele. Tulevikku seatud kuupäev avaldab postituse
              automaatselt sel ajal.
            </p>
          </section>
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-3">
          {!isNew && !confirmDelete && (
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition"
            >
              <Trash2 className="w-4 h-4" />
              Kustuta
            </button>
          )}
          {!isNew && confirmDelete && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Kindel?</span>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Kustutan...' : 'Jah, kustuta'}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-3 py-2 text-gray-600 rounded-lg text-sm hover:bg-gray-100"
              >
                Tühista
              </button>
            </div>
          )}
          {isNew && <span />}

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#4FC1E9] text-white rounded-lg text-sm font-medium hover:bg-[#3ba8d0] transition disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Salvestan...' : isNew ? 'Loo postitus' : 'Salvesta'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
